// src/pages/AdminDashboard.js
import React, { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  fetchArticles,
  createArticle,
  updateArticle,
  deleteArticle,
  fetchPendingArticles,
  updateArticleReview,
} from "../utils/api";
import TipTapEditor from "../components/TipTapEditor";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Trash2, Edit2, Save, Eye, EyeOff, Search, Check, X, Clock, FileText, LayoutDashboard } from "lucide-react";

// Shared PageStyles component for consistent styling
const PageStyles = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Azonix&display=swap');
    
    :root {
      --az: #a78bfa;
      --jp: #fbbf24;
      --wt: #06b6d4;
      --neu: #8b5cf6;
      --bg: #08080c;
      --glass: rgba(255, 255, 255, 0.03);
      --gb: rgba(167, 139, 250, 0.15);
      --card-bg: linear-gradient(135deg, rgba(20, 20, 30, 0.9), rgba(10, 10, 15, 0.95));
      --accent-gradient: linear-gradient(135deg, #a78bfa 0%, #6366f1 50%, #8b5cf6 100%);
    }
    
    * {
      scrollbar-width: thin;
      scrollbar-color: var(--az) var(--bg);
    }
    
    *::-webkit-scrollbar {
      width: 8px;
      height: 8px;
    }
    
    *::-webkit-scrollbar-track {
      background: var(--bg);
    }
    
    *::-webkit-scrollbar-thumb {
      background: var(--az);
      border-radius: 4px;
    }
    
    .font-azonix {
      font-family: 'Azonix', sans-serif;
    }
    
    .animate-float {
      animation: float 6s ease-in-out infinite;
    }
    
    @keyframes float {
      0%, 100% { transform: translateY(0px); }
      50% { transform: translateY(-10px); }
    }
    
    .glass-card {
      background: var(--glass);
      backdrop-filter: blur(20px);
      border: 1px solid var(--gb);
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.05);
    }
    
    .glow-border {
      position: relative;
    }
    
    .glow-border::before {
      content: '';
      position: absolute;
      inset: 0;
      border-radius: inherit;
      padding: 1px;
      background: linear-gradient(135deg, rgba(167, 139, 250, 0.5), rgba(99, 102, 241, 0.3), rgba(139, 92, 246, 0.5));
      -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
      mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
      -webkit-mask-composite: xor;
      mask-composite: exclude;
      pointer-events: none;
    }
    
    .gradient-text {
      background: linear-gradient(135deg, #a78bfa 0%, #6366f1 50%, #8b5cf6 100%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
    }
    
    .gradient-glow {
      background: linear-gradient(135deg, rgba(167, 139, 250, 0.1) 0%, rgba(99, 102, 241, 0.05) 100%);
    }
    
    .section-header {
      position: relative;
      display: inline-flex;
      align-items: center;
      gap: 12px;
      margin-bottom: 24px;
    }
    
    .section-header::before {
      content: '';
      position: absolute;
      left: 0;
      top: 50%;
      transform: translateY(-50%);
      width: 4px;
      height: 70%;
      background: linear-gradient(180deg, var(--az), transparent);
      border-radius: 2px;
    }
    
    input, textarea {
      background: rgba(0, 0, 0, 0.3) !important;
      border: 1px solid rgba(167, 139, 250, 0.2) !important;
      color: #fff !important;
    }
    
    input:focus, textarea:focus {
      border-color: var(--az) !important;
      box-shadow: 0 0 15px rgba(167, 139, 250, 0.3) !important;
      outline: none !important;
    }
    
    input::placeholder, textarea::placeholder {
      color: rgba(255, 255, 255, 0.4) !important;
    }
  `}</style>
);

export default function AdminDashboard() {
  const [articles, setArticles] = useState([]);
  const [pendingArticles, setPendingArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [articleForm, setArticleForm] = useState({
    title: "",
    excerpt: "",
    category: "",
    author: "",
    tags: [],
    featured_image: "",
    content: "",
    is_featured: false,
    is_published: false,
    // SEO Fields
    seo_title: "",
    seo_description: "",
    seo_keywords: "",
    canonical_url: "",
    // Schedule publishing
    scheduled_publish: null,
  });
  const [autoSave] = useState(false);
  const [previewContent, setPreviewContent] = useState("");
  const [showPreview, setShowPreview] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [draftsVisible, setDraftsVisible] = useState(true);
  const [publishedVisible, setPublishedVisible] = useState(true);
  const [pendingVisible, setPendingVisible] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [activityLog, setActivityLog] = useState([]);
  const editorRef = useRef(null);

  const loadArticles = useCallback(async () => {
    try {
      setLoading(true);
      console.log("📚 Loading articles (published + drafts + pending)...");
      
      const [published, drafts, pending] = await Promise.all([
        fetchArticles({ is_published: true }),
        fetchArticles({ is_published: false }),
        fetchPendingArticles(),
      ]);
      
      const combined = [...(published || []), ...(drafts || [])];
      console.log(
        "✅ Articles loaded:",
        combined.length,
        "(published:", (published || []).length,
        "drafts:", (drafts || []).length,
        ")"
      );
      console.log("📝 Pending AI articles:", pending?.length || 0);
      
      setArticles(combined);
      setPendingArticles(pending?.articles || []);
    } catch (error) {
      console.error("❌ Error loading articles:", error);
      alert(`Error loading articles: ${error.message}`);
      setArticles([]);
      setPendingArticles([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadPendingArticles = async () => {
    try {
      const result = await fetchPendingArticles();
      setPendingArticles(result.articles || []);
    } catch (error) {
      console.error("❌ Error loading pending articles:", error);
    }
  };

  const handleReview = async (articleId, status) => {
    if (!window.confirm(`Are you sure you want to ${status} this article?`)) return;
    
    try {
      console.log(`✅ ${status} article:`, articleId);
      const result = await updateArticleReview(articleId, status);
      
      if (result) {
        alert(`Article ${status} successfully!`);
        loadArticles();
        loadPendingArticles();
      }
    } catch (error) {
      console.error(`❌ Error ${status} article:`, error);
      alert(`Error ${status} article: ${error.message}`);
    }
  };

  useEffect(() => {
    loadArticles();
  }, [loadArticles]);

  const resetForm = () => {
    setSelectedArticle(null);
    setArticleForm({
      title: "",
      excerpt: "",
      category: "",
      author: "",
      tags: [],
      featured_image: "",
      content: "",
      is_featured: false,
      is_published: false,
    });
    setPreviewContent("");
    setIsDirty(false);
  };

  const resetFormForNewArticle = () => {
    if (!selectedArticle) {
      resetForm();
    }
  };

  const handleEdit = (article) => {
    if (isDirty && selectedArticle) {
      const proceed = window.confirm("You have unsaved changes. Discard and load the selected article?");
      if (!proceed) return;
    }
    console.log("📝 Editing article:", article);
    setSelectedArticle(article);
    const formData = {
      title: article.title,
      excerpt: article.excerpt || "",
      category: article.category || "",
      author: article.author || "",
      tags: article.tags || [],
      featured_image: article.featured_image || "",
      content: article.content || "",
      is_featured: article.is_featured || false,
      is_published: article.is_published,
      // SEO Fields
      seo_title: article.seo_title || article.title || "",
      seo_description: article.seo_description || article.excerpt || "",
      seo_keywords: article.seo_keywords || (article.tags ? article.tags.join(', ') : ""),
      canonical_url: article.canonical_url || "",
      scheduled_publish: article.scheduled_publish || null,
    };
    console.log("📝 Setting form data:", formData);
    setArticleForm(formData);
    setPreviewContent(article.content || "");
    if (editorRef.current?.setContent) {
      editorRef.current.setContent(article.content || "");
    }
    setIsDirty(false);
  };

  const handleNewArticle = () => {
    if (isDirty) {
      const proceed = window.confirm("You have unsaved changes. Start a new article and discard them?");
      if (!proceed) return;
    }
    resetFormForNewArticle();
    if (editorRef.current?.setContent) {
      editorRef.current.setContent("");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this article?")) return;
    
    try {
      console.log("🗑️ Deleting article:", id);
      const result = await deleteArticle(id);
      
      if (result) {
        console.log("✅ Article deleted successfully");
        alert("Article deleted successfully!");
        loadArticles();
      } else {
        console.error("❌ Failed to delete article - no result returned");
        alert("Failed to delete article. Please check the console for details.");
      }
    } catch (error) {
      console.error("❌ Error deleting article:", error);
      alert(`Error deleting article: ${error.message}`);
    }
  };

  const handleSave = useCallback(async () => {
    if (saving) return;

    try {
      setSaving(true);
      if (!articleForm.title?.trim()) {
        alert("Please enter a title for the article.");
        return;
      }

      if (!articleForm.excerpt?.trim()) {
        alert("Please enter an excerpt for the article.");
        return;
      }

      if (!articleForm.category?.trim()) {
        alert("Please select a category for the article.");
        return;
      }

      const content = editorRef.current?.getHTML() || "";
      if (!content.trim() || content === "<p></p>") {
        alert("Please add some content to the article.");
        return;
      }

      const payload = {
        ...articleForm,
        author: (articleForm.author && articleForm.author.trim()) ? articleForm.author.trim() : "ANIMAC",
        content,
        is_featured: !!articleForm.is_featured,
        is_published: !!articleForm.is_published,
      };

      console.log("💾 Saving article with payload:", payload);

      let result;
      if (selectedArticle) {
        console.log("📝 Updating article:", selectedArticle.id);
        result = await updateArticle(selectedArticle.id, payload);
      } else {
        console.log("➕ Creating new article");
        result = await createArticle(payload);
      }

      if (result) {
        console.log("✅ Article saved successfully:", result);
        
        // Add to activity log
        const newLog = {
          id: Date.now(),
          action: selectedArticle ? 'Updated' : 'Created',
          articleTitle: payload.title,
          timestamp: new Date().toISOString(),
        };
        setActivityLog(prev => [newLog, ...prev].slice(0, 20)); // Keep last 20 entries
        
        if (selectedArticle) {
          setArticleForm({
            ...payload,
            content: result.content || payload.content,
          });
          if (editorRef.current?.setContent) {
            editorRef.current.setContent(result.content || payload.content);
          }
          setPreviewContent(result.content || payload.content);
          alert("Article updated successfully!");
        } else {
          resetForm();
          alert("Article created successfully!");
        }
        
        loadArticles();
        setIsDirty(false);
      } else {
        console.error("❌ Failed to save article - no result returned");
        alert("Failed to save article. Please check the console for details.");
      }
    } catch (error) {
      console.error("❌ Error saving article:", error);
      alert(`Error saving article: ${error.message}`);
    } finally {
      setSaving(false);
    }
  }, [articleForm, selectedArticle, saving, loadArticles]);

  useEffect(() => {
    if (!autoSave || !selectedArticle) return;

    const interval = setInterval(() => {
      handleSave();
    }, 30000);

    return () => clearInterval(interval);
  }, [autoSave, selectedArticle, handleSave]);

  const handleEditorChange = (data) => {
    if (typeof data === 'string') {
      setPreviewContent(data);
      setArticleForm({ ...articleForm, content: data });
    } else {
      setPreviewContent(data.content);
      setArticleForm((prev) => ({ 
        ...prev, 
        content: data.content,
        title: data.title,
        excerpt: data.excerpt || '',
        featured_image: data.featuredImage,
        tags: data.tags,
        is_featured: data.isFeatured,
        category: data.category
      }));
    }
    setIsDirty(true);
  };

  const filteredArticles = articles.filter((a) =>
    a.title.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const draftArticles = filteredArticles.filter((a) => !a?.is_published);
  const publishedArticles = filteredArticles.filter((a) => a?.is_published);

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  if (loading) {
    return (
      <>
        <PageStyles />
        <div className="min-h-screen relative overflow-hidden" style={{ background: '#08080c' }}>
          <div className="absolute inset-0 gradient-glow"></div>
          <div className="relative z-10 flex items-center justify-center min-h-screen">
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 relative">
                <div className="absolute inset-0 rounded-full border-2 border-transparent border-t-[#a78bfa] animate-spin"></div>
                <LayoutDashboard className="w-8 h-8 text-[#a78bfa] absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
              </div>
              <p className="text-[#a78bfa] font-azonix text-xl">Loading Admin Dashboard...</p>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <PageStyles />
      <div className="min-h-screen relative overflow-hidden" style={{ background: '#08080c' }}>
        {/* Background effects */}
        <div className="absolute inset-0 gradient-glow"></div>
        <div className="absolute inset-0" style={{
          backgroundImage: `
            radial-gradient(circle at 20% 80%, rgba(167, 139, 250, 0.08) 0%, transparent 50%),
            radial-gradient(circle at 80% 20%, rgba(99, 102, 241, 0.08) 0%, transparent 50%),
            radial-gradient(circle at 50% 50%, rgba(139, 92, 246, 0.05) 0%, transparent 70%)
          `
        }}></div>
        
        <div className="relative z-10 p-6 pt-20">
          {/* Page Header */}
          <motion.div 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mb-8"
          >
            <h1 className="text-4xl font-bold font-azonix gradient-text flex items-center gap-4">
              <LayoutDashboard className="w-10 h-10 text-[#a78bfa]" />
              Admin Dashboard
            </h1>
            <p className="text-gray-400 mt-2 ml-14">Manage your articles, drafts, and AI-generated content</p>
          </motion.div>

          {/* Search & Filters */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="glass-card rounded-xl p-4 mb-6 glow-border"
          >
            <div className="flex flex-wrap gap-4 items-center">
              <div className="flex-1 min-w-[200px]">
                <Input
                  placeholder="Search articles..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full"
                  icon={<Search size={16} />}
                />
              </div>
              <div className="flex gap-2 flex-wrap">
                <Button
                  className="glass-card hover:bg-[rgba(167,139,250,0.2)] border border-[rgba(167,139,250,0.3)] text-white px-4 py-2 rounded-lg transition-all duration-300"
                  onClick={() => setShowPreview(!showPreview)}
                >
                  {showPreview ? <Eye size={16} /> : <EyeOff size={16} />} Preview
                </Button>
                <Button
                  className="glass-card hover:bg-[rgba(167,139,250,0.2)] border border-[rgba(167,139,250,0.3)] text-white px-4 py-2 rounded-lg transition-all duration-300"
                  onClick={() => setDraftsVisible(!draftsVisible)}
                >
                  {draftsVisible ? <Eye size={16} /> : <EyeOff size={16} />} Drafts
                </Button>
                <Button
                  className="glass-card hover:bg-[rgba(167,139,250,0.2)] border border-[rgba(167,139,250,0.3)] text-white px-4 py-2 rounded-lg transition-all duration-300"
                  onClick={() => setPublishedVisible(!publishedVisible)}
                >
                  {publishedVisible ? <Eye size={16} /> : <EyeOff size={16} />} Published
                </Button>
              </div>
            </div>
          </motion.div>

          {/* Editor + Preview */}
          <div className="flex flex-col lg:flex-row gap-6 mb-8">
            {/* Editor */}
            <motion.div 
              className={showPreview ? "flex-1" : "w-full"}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <div className="glass-card rounded-xl p-6 glow-border">
                <div className="mb-4">
                  <h2 className="font-azonix text-xl font-bold gradient-text flex items-center gap-2">
                    <FileText className="w-5 h-5" />
                    Article Editor
                  </h2>
                  <p className="text-gray-400 text-sm ml-7">Write your article content below</p>
                </div>
                <TipTapEditor
                  key={selectedArticle?.id || 'new-article'}
                  ref={editorRef}
                  initialContent={previewContent}
                  initialTitle={articleForm.title}
                  initialExcerpt={articleForm.excerpt}
                  initialFeaturedImage={articleForm.featured_image}
                  initialTags={articleForm.tags}
                  initialIsFeatured={articleForm.is_featured}
                  initialCategory={articleForm.category}
                  onUpdate={handleEditorChange}
                />
                
                {/* SEO & Publishing Panel */}
                <div className="mt-6 p-4 rounded-lg" style={{ background: 'rgba(167, 139, 250, 0.05)', border: '1px solid rgba(167, 139, 250, 0.2)' }}>
                  <h3 className="font-azonix font-semibold text-white mb-4 flex items-center gap-2">
                    <Search size={16} className="text-[#a78bfa]" />
                    SEO & Publishing Settings
                  </h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs text-gray-400 mb-1 uppercase tracking-wide">SEO Title</label>
                      <input
                        type="text"
                        value={articleForm.seo_title || ''}
                        onChange={(e) => setArticleForm({ ...articleForm, seo_title: e.target.value })}
                        placeholder="Custom SEO title (optional)"
                        className="w-full px-3 py-2 rounded-md text-sm"
                      />
                      <span className="text-xs text-gray-500">{(articleForm.seo_title || articleForm.title || '').length}/60</span>
                    </div>
                    
                    <div>
                      <label className="block text-xs text-gray-400 mb-1 uppercase tracking-wide">Canonical URL</label>
                      <input
                        type="text"
                        value={articleForm.canonical_url || ''}
                        onChange={(e) => setArticleForm({ ...articleForm, canonical_url: e.target.value })}
                        placeholder="https://example.com/article"
                        className="w-full px-3 py-2 rounded-md text-sm"
                      />
                    </div>
                    
                    <div className="md:col-span-2">
                      <label className="block text-xs text-gray-400 mb-1 uppercase tracking-wide">SEO Description</label>
                      <textarea
                        value={articleForm.seo_description || ''}
                        onChange={(e) => setArticleForm({ ...articleForm, seo_description: e.target.value })}
                        placeholder="Custom meta description for search engines..."
                        className="w-full px-3 py-2 rounded-md text-sm"
                        rows={2}
                      />
                      <span className="text-xs text-gray-500">{(articleForm.seo_description || articleForm.excerpt || '').length}/160</span>
                    </div>
                    
                    <div className="md:col-span-2">
                      <label className="block text-xs text-gray-400 mb-1 uppercase tracking-wide">SEO Keywords</label>
                      <input
                        type="text"
                        value={articleForm.seo_keywords || ''}
                        onChange={(e) => setArticleForm({ ...articleForm, seo_keywords: e.target.value })}
                        placeholder="keyword1, keyword2, keyword3"
                        className="w-full px-3 py-2 rounded-md text-sm"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-xs text-gray-400 mb-1 uppercase tracking-wide">Schedule Publish</label>
                      <input
                        type="datetime-local"
                        value={articleForm.scheduled_publish || ''}
                        onChange={(e) => setArticleForm({ ...articleForm, scheduled_publish: e.target.value })}
                        className="w-full px-3 py-2 rounded-md text-sm"
                      />
                    </div>
                    
                    <div className="flex items-center gap-3">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={articleForm.is_published}
                          onChange={(e) => setArticleForm({ ...articleForm, is_published: e.target.checked })}
                          className="w-4 h-4 rounded"
                        />
                        <span className="text-sm text-gray-300">Publish immediately</span>
                      </label>
                    </div>
                  </div>
                </div>
                
                <div className="flex mt-4 gap-3 flex-wrap">
                  <Button
                    className="flex-1 bg-gradient-to-r from-green-600 to-green-500 hover:from-green-500 hover:to-green-400 text-white flex items-center gap-2 transition-all duration-300 rounded-lg"
                    onClick={handleNewArticle}
                  >
                    ✨ New Article
                  </Button>
                  <Button
                    className="bg-gradient-to-r from-[#a78bfa] to-[#6366f1] hover:from-[#6366f1] hover:to-[#8b5cf6] text-white flex items-center gap-2 transition-all duration-300 rounded-lg px-6"
                    onClick={handleSave}
                    disabled={saving}
                  >
                    <Save size={16} /> {saving ? "Saving..." : (selectedArticle ? "Update Article" : "Save Article")}
                  </Button>
                </div>
              </div>
            </motion.div>

            {/* Preview */}
            {showPreview && (
              <motion.div 
                className="flex-1" 
                initial="hidden" 
                animate="visible" 
                variants={cardVariants}
                transition={{ duration: 0.5, delay: 0.3 }}
              >
                <Card className="glass-card rounded-xl glow-border h-full">
                  <CardContent className="p-6">
                    <h2 className="font-azonix text-xl font-bold gradient-text mb-4 flex items-center gap-2">
                      <Eye className="w-5 h-5" />
                      Live Preview
                    </h2>
                    {articleForm.title && (
                      <h1 className="text-2xl font-bold gradient-text mb-4">
                        {articleForm.title}
                      </h1>
                    )}
                    {articleForm.featured_image && (
                      <img
                        src={articleForm.featured_image}
                        alt="Featured"
                        className="w-full max-h-64 object-cover rounded-lg mb-4 shadow-lg"
                      />
                    )}
                    <div
                      className="prose prose-invert max-w-full overflow-auto rounded-lg p-4"
                      style={{ background: 'rgba(0, 0, 0, 0.3)', border: '1px solid rgba(167, 139, 250, 0.2)' }}
                      dangerouslySetInnerHTML={{ __html: previewContent }}
                    />
                  </CardContent>
                </Card>
              </motion.div>
            )}
          </div>

          {/* Pending AI Reviews Section */}
          {pendingArticles.length > 0 && (
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="mb-8"
            >
              <div className="flex items-center gap-3 mb-4 section-header">
                <div className="relative">
                  <div className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full animate-ping"></div>
                  <div className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full"></div>
                  <Clock size={24} className="text-[#a78bfa]" />
                </div>
                <h2 className="text-2xl font-bold font-azonix gradient-text">
                  Pending AI Review
                </h2>
                <span className="bg-[rgba(167,139,250,0.2)] text-[#a78bfa] px-3 py-1 rounded-full text-sm font-semibold border border-[rgba(167,139,250,0.3)]">
                  {pendingArticles.length} articles
                </span>
              </div>
              
              <Button
                className="w-full text-left glass-card bg-[rgba(167,139,250,0.1)] border border-[rgba(167,139,250,0.3)] mb-4 px-4 py-3 flex justify-between items-center rounded-xl hover:bg-[rgba(167,139,250,0.2)] transition-all duration-300"
                onClick={() => setPendingVisible(!pendingVisible)}
              >
                <span className="flex items-center gap-2 text-white">
                  <FileText size={16} className="text-[#a78bfa]" />
                  AI-Generated Articles Awaiting Review
                </span>
                {pendingVisible ? <Eye size={16} className="text-[#a78bfa]" /> : <EyeOff size={16} className="text-[#a78bfa]" />}
              </Button>

              <AnimatePresence>
                {pendingVisible && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="space-y-3"
                  >
                    {pendingArticles.map((article, index) => (
                      <motion.div
                        key={article.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1 }}
                      >
                        <Card
                          className="p-4 glass-card rounded-xl border border-[rgba(167,139,250,0.3)] hover:shadow-lg hover:shadow-[rgba(167,139,250,0.2)] transition-all duration-300"
                        >
                          <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-4">
                            <div className="flex-1">
                              <div className="flex flex-wrap gap-2 mb-3">
                                <span className="text-xs px-2 py-1 bg-[rgba(167,139,250,0.2)] border border-[rgba(167,139,250,0.4)] text-[#a78bfa] rounded">
                                  🤖 AI Generated
                                </span>
                                <span className="text-xs px-2 py-1 bg-yellow-500/20 border border-yellow-400 text-yellow-300 rounded">
                                  ⏳ Pending Review
                                </span>
                                {article.category && (
                                  <span className="text-xs px-2 py-1 bg-blue-500/20 border border-blue-400 text-blue-300 rounded">
                                    {article.category}
                                  </span>
                                )}
                              </div>
                              
                              <h3 className="font-azonix font-semibold text-white text-lg mb-2">
                                {article.title}
                              </h3>
                              
                              {article.excerpt && (
                                <p className="text-gray-400 text-sm mb-2 line-clamp-2">
                                  {article.excerpt}
                                </p>
                              )}
                              
                              <div className="flex flex-wrap gap-2 text-xs text-gray-500">
                                <span>📅 {new Date(article.created_at).toLocaleDateString()}</span>
                                <span>✍️ {article.author || 'AI Content Team'}</span>
                                {article.original_source && (
                                  <span>📡 Source: {article.original_source}</span>
                                )}
                                {article.tags && article.tags.length > 0 && (
                                  <span>🏷️ {article.tags.join(', ')}</span>
                                )}
                              </div>
                            </div>

                            <div className="flex flex-wrap gap-2">
                              <button
                                onClick={() => handleEdit(article)}
                                className="flex items-center gap-1 px-3 py-1 bg-blue-500/20 border border-blue-400 text-blue-300 rounded hover:bg-blue-500/40 transition-colors"
                                title="Edit this article"
                              >
                                <Edit2 size={14} />
                                Edit
                              </button>
                              <button
                                onClick={() => handleReview(article.id, 'approved')}
                                className="flex items-center gap-1 px-3 py-1 bg-green-500/20 border border-green-400 text-green-300 rounded hover:bg-green-500/40 transition-colors"
                                title="Approve and publish"
                              >
                                <Check size={14} />
                                Approve
                              </button>
                              <button
                                onClick={() => handleReview(article.id, 'rejected')}
                                className="flex items-center gap-1 px-3 py-1 bg-red-500/20 border border-red-400 text-red-300 rounded hover:bg-red-500/40 transition-colors"
                                title="Reject this article"
                              >
                                <X size={14} />
                                Reject
                              </button>
                            </div>
                          </div>
                        </Card>
                      </motion.div>
                    ))}

                    <div className="flex justify-center pt-4">
                      <Button
                        onClick={loadPendingArticles}
                        className="glass-card border border-[rgba(167,139,250,0.3)] hover:bg-[rgba(167,139,250,0.2)] text-white px-4 py-2 rounded-lg text-sm transition-all duration-300"
                      >
                        🔄 Refresh Pending Articles
                      </Button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          )}

          {/* Articles List */}
          <div className="space-y-6">
            {draftArticles.length > 0 && (
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.5 }}
              >
                <Button
                  className="w-full text-left glass-card border border-[rgba(167,139,250,0.3)] mb-4 px-4 py-3 flex justify-between items-center rounded-xl hover:bg-[rgba(167,139,250,0.2)] transition-all duration-300"
                  onClick={() => setDraftsVisible(!draftsVisible)}
                >
                  <span className="text-white flex items-center gap-2">
                    <FileText size={16} className="text-[#a78bfa]" />
                    Drafts ({draftArticles.length})
                  </span>
                  {draftsVisible ? <Eye size={16} className="text-[#a78bfa]" /> : <EyeOff size={16} className="text-[#a78bfa]" />}
                </Button>

                <AnimatePresence>
                  {draftsVisible && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="space-y-2"
                    >
                      {draftArticles.map((article, index) => (
                        <motion.div
                          key={article.id}
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.05 }}
                        >
                          <Card
                            className="flex justify-between items-center p-4 glass-card rounded-xl border border-[rgba(167,139,250,0.2)] hover:shadow-lg transition-all duration-300"
                          >
                            <div>
                              <h3 className="font-azonix font-semibold text-white">{article.title}</h3>

                              <div className="flex flex-wrap gap-2 mt-2">
                                {!article.is_published && (
                                  <span className="text-xs px-2 py-1 border border-red-500 text-red-500 rounded bg-red-500/10">
                                    Draft
                                  </span>
                                )}
                                {article.is_published && (
                                  <span className="text-xs px-2 py-1 border border-green-500 text-green-500 rounded bg-green-500/10">
                                    Published
                                  </span>
                                )}
                                {article.is_featured && (
                                  <span className="text-xs px-2 py-1 border border-yellow-400 text-yellow-400 rounded bg-yellow-400/10">
                                    Featured
                                  </span>
                                )}
                              </div>

                              <div className="text-sm text-gray-400 mt-2">
                                {article.category} {article.tags?.length > 0 && `| ${article.tags.join(", ")}`}
                              </div>
                            </div>

                            <div className="flex gap-2">
                              <button
                                onClick={() => handleEdit(article)}
                                className="p-2 rounded-lg hover:bg-[rgba(167,139,250,0.2)] transition-colors duration-300 border border-transparent hover:border-[rgba(167,139,250,0.3)]"
                              >
                                <Edit2 size={18} className="text-gray-200 hover:text-white" />
                              </button>
                              <button
                                onClick={() => handleDelete(article.id)}
                                className="p-2 rounded-lg hover:bg-red-500/20 transition-colors duration-300 border border-transparent hover:border-red-500/30"
                              >
                                <Trash2 size={18} className="text-gray-200 hover:text-red-400" />
                              </button>
                            </div>
                          </Card>
                        </motion.div>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            )}

            {publishedArticles.length > 0 && (
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.6 }}
              >
                <Button
                  className="w-full text-left glass-card border border-[rgba(167,139,250,0.3)] mb-4 px-4 py-3 flex justify-between items-center rounded-xl hover:bg-[rgba(167,139,250,0.2)] transition-all duration-300"
                  onClick={() => setPublishedVisible(!publishedVisible)}
                >
                  <span className="text-white flex items-center gap-2">
                    <FileText size={16} className="text-[#a78bfa]" />
                    Published ({publishedArticles.length})
                  </span>
                  {publishedVisible ? <Eye size={16} className="text-[#a78bfa]" /> : <EyeOff size={16} className="text-[#a78bfa]" />}
                </Button>

                <AnimatePresence>
                  {publishedVisible && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="space-y-2"
                    >
                      {publishedArticles.map((article, index) => (
                        <motion.div
                          key={article.id}
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.05 }}
                        >
                          <Card
                            className="flex justify-between items-center p-4 glass-card rounded-xl border border-[rgba(167,139,250,0.2)] hover:shadow-lg transition-all duration-300"
                          >
                            <div>
                              <h3 className="font-azonix font-semibold text-white">{article.title}</h3>

                              <div className="flex flex-wrap gap-2 mt-2">
                                {!article.is_published && (
                                  <span className="text-xs px-2 py-1 border border-red-500 text-red-500 rounded bg-red-500/10">
                                    Draft
                                  </span>
                                )}
                                {article.is_published && (
                                  <span className="text-xs px-2 py-1 border border-green-500 text-green-500 rounded bg-green-500/10">
                                    Published
                                  </span>
                                )}
                                {article.is_featured && (
                                  <span className="text-xs px-2 py-1 border border-yellow-400 text-yellow-400 rounded bg-yellow-400/10">
                                    Featured
                                  </span>
                                )}
                              </div>

                              <div className="text-sm text-gray-400 mt-2">
                                {article.category} {article.tags?.length > 0 && `| ${article.tags.join(", ")}`}
                              </div>
                            </div>

                            <div className="flex gap-2">
                              <button
                                onClick={() => handleEdit(article)}
                                className="p-2 rounded-lg hover:bg-[rgba(167,139,250,0.2)] transition-colors duration-300 border border-transparent hover:border-[rgba(167,139,250,0.3)]"
                              >
                                <Edit2 size={18} className="text-gray-200 hover:text-white" />
                              </button>
                              <button
                                onClick={() => handleDelete(article.id)}
                                className="p-2 rounded-lg hover:bg-red-500/20 transition-colors duration-300 border border-transparent hover:border-red-500/30"
                              >
                                <Trash2 size={18} className="text-gray-200 hover:text-red-400" />
                              </button>
                            </div>
                          </Card>
                        </motion.div>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            )}

          </div>
        </div>
      </div>

      {/* Activity Log Panel */}
      {activityLog.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.7 }}
          className="mt-8"
        >
          <div className="flex items-center gap-3 mb-4 section-header">
            <Clock size={24} className="text-[#a78bfa]" />
            <h2 className="text-2xl font-bold font-azonix gradient-text">
              Activity Log
            </h2>
          </div>
          
          <div className="glass-card rounded-xl p-4 glow-border">
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {activityLog.map((log) => (
                <div 
                  key={log.id}
                  className="flex items-center gap-3 p-2 rounded-lg hover:bg-[rgba(167,139,250,0.05)] transition-colors"
                >
                  <span className="text-xs text-gray-500 w-32">
                    {new Date(log.timestamp).toLocaleTimeString()}
                  </span>
                  <span className={`text-xs px-2 py-0.5 rounded ${
                    log.action === 'Created' 
                      ? 'bg-green-500/20 text-green-400' 
                      : 'bg-blue-500/20 text-blue-400'
                  }`}>
                    {log.action}
                  </span>
                  <span className="text-sm text-gray-300 truncate flex-1">
                    {log.articleTitle}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      )}
    </>
  );
}
